import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';

interface AnalyticsMetrics {
  totalSessions: number;
  activeSessions: number;
  completedSessions: number;
  totalVotes: number;
  averageVotesPerSession: number;
  totalVariants: number;
  winningVariants: number;
}

interface SessionPerformance {
  sessionId: string;
  sessionName: string;
  totalVotes: number;
  variantCount: number;
  status: string;
  createdAt: string;
}

interface VoterTypeDistribution {
  voterType: string;
  count: number;
}

export const useABTestAnalytics = () => {
  const { user } = useSecureAuthContext();
  const [metrics, setMetrics] = useState<AnalyticsMetrics>({
    totalSessions: 0,
    activeSessions: 0,
    completedSessions: 0,
    totalVotes: 0,
    averageVotesPerSession: 0,
    totalVariants: 0,
    winningVariants: 0,
  });
  const [sessionPerformance, setSessionPerformance] = useState<SessionPerformance[]>([]);
  const [voterDistribution, setVoterDistribution] = useState<VoterTypeDistribution[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    
    fetchAnalytics();
  }, [user?.id]);

  const fetchAnalytics = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Fetch all sessions
      const { data: sessions, error: sessionsError } = await supabase
        .from('ab_test_sessions')
        .select('*')
        .eq('user_id', user.id);

      if (sessionsError) throw sessionsError;

      // Fetch all variants for user's sessions
      const sessionIds = sessions?.map(s => s.id) || [];
      const { data: variants, error: variantsError } = await supabase
        .from('ab_test_variants')
        .select('*')
        .in('session_id', sessionIds);

      if (variantsError) throw variantsError;

      // Fetch all votes for user's sessions
      const { data: votes, error: votesError } = await supabase
        .from('ab_test_votes')
        .select('*')
        .in('session_id', sessionIds);

      if (votesError) throw votesError;

      // Calculate metrics
      const activeSessions = sessions?.filter(s => s.status === 'active').length || 0;
      const completedSessions = sessions?.filter(s => s.status === 'completed').length || 0;
      const totalVotes = votes?.length || 0;
      const winningVariants = variants?.filter(v => v.is_winner).length || 0;

      setMetrics({
        totalSessions: sessions?.length || 0,
        activeSessions,
        completedSessions,
        totalVotes,
        averageVotesPerSession: sessions?.length ? totalVotes / sessions.length : 0,
        totalVariants: variants?.length || 0,
        winningVariants,
      });

      // Calculate session performance
      const performance: SessionPerformance[] = sessions?.map(session => {
        const sessionVotes = votes?.filter(v => v.session_id === session.id).length || 0;
        const sessionVariants = variants?.filter(v => v.session_id === session.id).length || 0;
        
        return {
          sessionId: session.id,
          sessionName: session.name,
          totalVotes: sessionVotes,
          variantCount: sessionVariants,
          status: session.status,
          createdAt: session.created_at,
        };
      }) || [];

      setSessionPerformance(performance);

      // Calculate voter type distribution
      const voterTypes = votes?.reduce((acc, vote) => {
        acc[vote.voter_type] = (acc[vote.voter_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      const distribution: VoterTypeDistribution[] = Object.entries(voterTypes).map(([type, count]) => ({
        voterType: type,
        count,
      }));

      setVoterDistribution(distribution);

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    metrics,
    sessionPerformance,
    voterDistribution,
    loading,
    refetch: fetchAnalytics,
  };
};
